import {
  Box,
  Icon,
  Skeleton,
  Text,
  Tooltip,
  TooltipTrigger,
  TooltipPopup,
} from '@salestrak/ui'
import type { Omittable } from "@salestrak/ui";

type KpiCardProps = Omittable<{
  title: string
  value?: string | number | null
  subtitle?: string
  trend?: 'up' | 'down' | 'flat'
  danger?: boolean
  clickable?: boolean
  tooltip?: string
  onClick?: () => void
  loading?: boolean
}>;

export function KpiCard({ title, value, subtitle, trend, danger, clickable, tooltip, onClick, loading }: KpiCardProps) {
  const trendColor = trend === "up" ? "green" : trend === "down" ? "red" : "muted"

  const card = (
    <Box surface="card" rounded="xl" border padding="md">
      <Box direction="row" justify="between" align="center">
        <Box direction="row" gap="xs" align="center">
          <Text size="xs" weight="medium" variant="muted">
            {title.toUpperCase()}
          </Text>
          {tooltip && !onClick && (
            <Tooltip>
              <TooltipTrigger>
                <Icon name="info" size="xs" color="muted" />
              </TooltipTrigger>
              <TooltipPopup side="top">
                <Box maxWidth={240}>
                  <Text size="xs">{tooltip}</Text>
                </Box>
              </TooltipPopup>
            </Tooltip>
          )}
        </Box>
        {clickable && (
          <Icon name="chevron-right" size="xs" color="muted" />
        )}
      </Box>
      <Box paddingTop="xs">
        {loading ? (
          <>
            <Skeleton className="h-8 w-20" />
            {subtitle !== undefined && <Box paddingTop="xs"><Skeleton className="h-4 w-32" /></Box>}
          </>
        ) : (
          <Box direction="row" justify="between" align="end">
            <Text size="2xl" weight="bold" variant={danger ? "destructive" : undefined}>
              {typeof value === 'number' ? value.toLocaleString() : (value ?? '—')}
            </Text>
            {subtitle && (
              <Box direction="row" gap="xs" align="center">
                {trend && trend !== 'flat' && (
                  <Icon
                    name={trend === 'up' ? 'arrow-up' : 'arrow-down'}
                    size="xs"
                    color={trend === "up" ? "light-green" : "red"}
                  />
                )}
                <Text size="sm" weight="semibold" color={trendColor}>
                  {subtitle}
                </Text>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  )

  if (onClick) {
    return (
      <Box as="button" onClick={onClick} fill="width">
        {card}
      </Box>
    )
  }

  return card
}
